import type { ResponseError } from '@import-io/js-sdk';
import { isPresent } from '@import-io/typeguards';

import { FREE_PRICE, MONTHS_IN_YEAR, PLANS, VALID_PLANS } from 'features/signup/signup-constants';
import type { Plan, PlanPricing, SignupErrorBody } from 'features/signup/signup-types';
import { PlanType, RecurlyErrorCode, RecurlyErrorStatus } from 'features/signup/signup-types';

export const getRequiredRule = (text: string) => ({ required: true, message: `${text} is required` });

// prices are display strings like '$249 / month'
export const getPriceAmount = (price: string): number => Number(price.replace(/[^\d.]/g, '')) || 0;

export const formatAmount = (amount: number): string => `$${amount.toLocaleString('en-US')}`;

export const getPricing = (plan: Plan, isAnnual: boolean): PlanPricing => {
  const monthly = isAnnual ? plan.price_annual : plan.price;
  const monthlyAmount = getPriceAmount(monthly);
  const saved = (getPriceAmount(plan.price) - getPriceAmount(plan.price_annual)) * MONTHS_IN_YEAR;
  const discount = saved > 0 ? `${Math.round((saved / (getPriceAmount(plan.price) * MONTHS_IN_YEAR)) * 100)}%` : null;

  if (!isAnnual || plan.price === FREE_PRICE) {
    return {
      amount: plan.price === FREE_PRICE ? monthly : formatAmount(monthlyAmount),
      chargeLabel: monthly,
      discount: discount,
      monthly: monthly,
      savings: null,
      total: null,
    };
  }

  const total = monthlyAmount * MONTHS_IN_YEAR;

  return {
    amount: formatAmount(monthlyAmount),
    chargeLabel: `${formatAmount(total)} / year`,
    discount: discount,
    monthly: monthly,
    savings: saved > 0 ? formatAmount(saved) : null,
    total: formatAmount(total),
  };
};

// the url hash preselects plan and period: #standard_2026,annual — order and short aliases (#annual,standard) both work
export const parseSignupHash = (hash: string): { isAnnual: boolean; planType: PlanType } => {
  const tokens = hash.replace(/^#/, '').toLowerCase().split(',');
  const matched = tokens
    .map((token) => VALID_PLANS.find((plan) => plan === token || plan.startsWith(`${token}_`)))
    .find(isPresent);
  const planType = (matched as PlanType) ?? PlanType.TRIAL;

  return {
    isAnnual: tokens.includes('annual') && PLANS[planType].price !== FREE_PRICE,
    planType: planType,
  };
};

export const formatError = (message: string): string => `Error creating account: ${message}`;

export const getErrorMessage = (e: unknown): string => {
  const { message, body } = e as ResponseError<SignupErrorBody>;
  const { error, message: bodyMessage = message, recurlyError }: SignupErrorBody = isPresent(body) ? body : { error: message };
  if (isPresent(recurlyError)) {
    const { customerMessage, errorCode, status } = recurlyError;
    return formatError(
      errorCode === RecurlyErrorCode.DECLINED || status === RecurlyErrorStatus.ERROR
        ? customerMessage
        : isPresent(error)
        ? error
        : bodyMessage,
    );
  }

  return formatError(isPresent(error) ? error : bodyMessage);
};
